(() => {
  // Rozet (loyalty) storefront runtime.
  //
  // - Signed-in customers are reported to the rozet service once per session
  //   through the app proxy (/apps/rozet/join). Shopify signs that request
  //   with the logged-in customer's id; the browser never sends an id itself.
  // - The personal member code is applied to the cart only while signed in
  //   and only when no other discount code is in the cart (member codes
  //   combine with shipping discounts only). Signed out, a WMR- code is
  //   removed from the cart.
  // - Renders the rozet card in the mini cart and the member page.

  const STATE_NODE = document.getElementById('wm-rozet-state');
  if (!STATE_NODE) return;
  let state;
  try {
    state = JSON.parse(STATE_NODE.textContent);
  } catch (_) {
    return;
  }

  const TIERS = [
    { key: 'wemeet', name: 'WeMeet', min: 0, rate: '%5' },
    { key: 'wereturn', name: 'WeReturn', min: 1250, rate: '%7,5' },
    { key: 'weknow', name: 'WeKnow', min: 2500, rate: '%10' },
    { key: 'webelong', name: 'WeBelong', min: 3750, rate: '%15' },
    { key: 'wemoment', name: 'WeMoment', min: 5000, rate: '%20' }
  ];
  const MEMBER_PREFIX = 'WMR-';
  const KURUS_PER_POINT = 500;
  const JOIN_KEY = 'wm-rozet-joined';
  const root = window.Shopify?.routes?.root || '/';
  const launched = () => Date.now() >= Date.parse(state.launchAt);
  const isMemberCode = (code) => String(code || '').toUpperCase().startsWith(MEMBER_PREFIX);

  const tierFor = (points) => {
    let index = 0;
    TIERS.forEach((tier, i) => { if (points >= tier.min) index = i; });
    return index;
  };
  const currentTierIndex = () => {
    const byKey = TIERS.findIndex((tier) => tier.key === state.tier);
    return byKey >= 0 ? byKey : tierFor(Number(state.points) || 0);
  };
  const formatPoints = (value) => new Intl.NumberFormat('tr-TR').format(value);

  // ---------------------------------------------------------------------
  // Membership: tell the service this customer signed in (once per session).
  // ---------------------------------------------------------------------

  const readSession = (key) => {
    try { return sessionStorage.getItem(key); } catch (_) { return null; }
  };
  const writeSession = (key, value) => {
    try {
      if (value === null) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, value);
    } catch (_) {}
  };

  // ---------------------------------------------------------------------
  // Acquisition source: kept in this browser for 30 days and sent once with
  // the join call (the service records the first one per member). Ad clicks,
  // campaigns and outside referrers replace it; a plain direct visit does not.
  // ---------------------------------------------------------------------

  const SOURCE_KEY = 'wm-src';
  const SOURCE_DAYS = 30;

  function readSource() {
    try {
      const s = JSON.parse(localStorage.getItem(SOURCE_KEY) || 'null');
      if (s && Date.now() - Date.parse(s.at) < SOURCE_DAYS * 86400000) return s;
    } catch (_) {}
    return null;
  }

  function captureSource() {
    const p = new URLSearchParams(location.search);
    const pick = (k) => (p.get(k) || '').slice(0, 80);
    const own = /(^|\.)(wemondcoffee\.com|myshopify\.com|shopify\.com|shop\.app)$/i;
    let ref = '';
    try { ref = document.referrer ? new URL(document.referrer).hostname : ''; } catch (_) {}
    if (own.test(ref)) ref = '';
    const touch = {
      s: pick('utm_source'), m: pick('utm_medium'), c: pick('utm_campaign'), t: pick('utm_content'),
      r: ref.slice(0, 120),
      k: p.has('fbclid') ? 'fb' : (p.has('gclid') || p.has('gbraid') || p.has('wbraid')) ? 'g' : '',
      at: new Date().toISOString()
    };
    const meaningful = touch.s || touch.c || touch.k || touch.r;
    if (!meaningful && readSource()) return; // a direct visit keeps the earlier source
    try { localStorage.setItem(SOURCE_KEY, JSON.stringify(touch)); } catch (_) {}
  }

  function sourceQuery() {
    const s = readSource();
    if (!s) return '';
    const q = new URLSearchParams();
    for (const [k, v] of [['src_s', s.s], ['src_m', s.m], ['src_c', s.c], ['src_t', s.t], ['src_r', s.r], ['src_k', s.k], ['src_at', s.at]]) {
      if (v) q.set(k, v);
    }
    return q.toString();
  }

  captureSource();

  async function join(marketing) {
    const params = new URLSearchParams(sourceQuery());
    if (marketing === true) params.set('marketing', '1');
    if (marketing === false) params.set('marketing', '0');
    const query = params.toString() ? `?${params}` : '';
    const response = await fetch(`${root}apps/rozet/join${query}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: '', // Shopify's app proxy rejects POSTs without a Content-Length
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`rozet join ${response.status}`);
    const result = await response.json();
    if (result && result.member) {
      if (typeof result.points === 'number') state.points = result.points;
      if (typeof result.tier === 'string') state.tier = result.tier;
      if (typeof result.code === 'string' && result.code) state.code = result.code;
      writeSession(JOIN_KEY, String(state.customerId));
    }
    return result;
  }

  // ---------------------------------------------------------------------
  // Cart: apply the member code while signed in, remove it when signed out.
  // ---------------------------------------------------------------------

  let cartBusy = false;
  const attempted = new Set();

  const appliedCodes = (cart) => {
    const codes = [];
    for (const entry of cart?.discount_codes || []) {
      if (entry?.code) codes.push({ code: String(entry.code), applicable: entry.applicable !== false });
    }
    if (codes.length === 0) {
      for (const entry of cart?.cart_level_discount_applications || []) {
        if (entry?.type === 'discount_code' && entry.title) codes.push({ code: String(entry.title), applicable: true });
      }
    }
    return codes;
  };

  async function fetchCart() {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error('cart');
    return response.json();
  }

  async function setCartDiscount(value) {
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: value })
    });
    if (!response.ok) throw new Error('discount');
  }

  // What the cart should do for this visitor; null when nothing.
  function cartAction(cart) {
    const codes = appliedCodes(cart);
    if (!state.signedIn) {
      return codes.some((c) => isMemberCode(c.code)) ? { remove: true } : null;
    }
    if (!state.code || !launched() || !(Number(cart?.item_count) > 0)) return null;
    if (codes.length > 0) return null; // the customer chose another code, or ours is already there
    if (attempted.has(state.code)) return null;
    return { apply: state.code };
  }

  async function reconcileCart(cart) {
    if (cartBusy) return false;
    const action = cartAction(cart);
    if (!action) return false;
    cartBusy = true;
    try {
      if (action.apply) attempted.add(action.apply);
      await setCartDiscount(action.remove ? '' : action.apply);
      if (window.WemondMiniCart?.refresh) await window.WemondMiniCart.refresh();
      document.dispatchEvent(new CustomEvent('wm-rozet:cart-updated'));
      return true;
    } catch (_) {
      return false;
    } finally {
      cartBusy = false;
    }
  }

  // ---------------------------------------------------------------------
  // Mini cart card (called by wemond-mini-cart.js on every render).
  // ---------------------------------------------------------------------

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  function cartPoints(cart) {
    let kurus = 0;
    for (const item of cart?.items || []) {
      if (item.gift_card) continue;
      if (!state.eligibleTypes.includes(item.product_type)) continue;
      kurus += Number(item.final_line_price) || 0;
    }
    return Math.floor((kurus + KURUS_PER_POINT / 2) / KURUS_PER_POINT);
  }

  function renderCart(cart, miniCart) {
    const host = miniCart?.querySelector?.('[data-wmc-rozet]');
    if (!host) return;
    const itemCount = Number(cart?.item_count) || 0;
    host.replaceChildren();
    host.hidden = itemCount === 0;
    if (itemCount === 0) return;

    if (!state.signedIn) {
      host.dataset.state = 'guest';
      host.append(
        el('strong', 'wm-rozet-mini__title', 'Üye indirimin seni bekliyor'),
        el('p', 'wm-rozet-mini__text', 'Giriş yap, rozetine göre %5’ten başlayan indirimin sepetine otomatik eklensin.')
      );
      const link = el('a', 'wm-rozet-mini__action', 'Giriş yap');
      link.href = state.loginUrl;
      host.append(link);
      reconcileCart(cart);
      return;
    }

    const tier = TIERS[currentTierIndex()];
    const codes = appliedCodes(cart);
    const ours = codes.find((c) => state.code && c.code.toUpperCase() === state.code.toUpperCase());
    const other = codes.find((c) => !isMemberCode(c.code));
    host.dataset.state = 'member';

    const head = el('div', 'wm-rozet-mini__head');
    head.append(el('span', 'wm-rozet-mini__badge', tier.name), el('strong', 'wm-rozet-mini__title', `${tier.rate} üye indirimi`));
    host.append(head);

    let message;
    if (!launched()) message = 'Rozet indirimleri 19 Ekim’de başlıyor. Üyeliğin hazır.';
    else if (ours && ours.applicable) message = 'Sepetine uygulandı. Kargo indirimleriyle birlikte geçerli.';
    else if (other) message = 'Başka bir indirim kodu kullanıyorsun. Rozet indirimin bu kodla birleşmez.';
    else if (!state.code) message = 'Kişisel indirimin hazırlanıyor, birazdan sepetine eklenecek.';
    else message = 'Rozet indirimi kahvelerde geçerli; Deneyim Paketleri hariç.';
    host.append(el('p', 'wm-rozet-mini__text', message));

    const points = cartPoints(cart);
    if (points > 0 && launched()) {
      host.append(el('p', 'wm-rozet-mini__points', `Bu siparişle yaklaşık +${formatPoints(points)} puan`));
    }
    reconcileCart(cart);
  }

  // ---------------------------------------------------------------------
  // Member page (sections/wemond-rozet-page.liquid).
  // ---------------------------------------------------------------------

  function renderPage() {
    const page = document.querySelector('[data-wm-rozet-page]');
    if (!page || !state.signedIn) return;
    const points = Number(state.points) || 0;
    const index = currentTierIndex();
    const tier = TIERS[index];
    const next = TIERS[index + 1];
    const set = (selector, text) => {
      const node = page.querySelector(selector);
      if (node) node.textContent = text;
    };
    set('[data-rozet-tier]', tier.name);
    set('[data-rozet-rate]', `${tier.rate} üye indirimi`);
    set('[data-rozet-points]', formatPoints(points));
    const bar = page.querySelector('[data-rozet-progress]');
    if (next) {
      const span = next.min - tier.min;
      const done = Math.min(1, Math.max(0, (points - tier.min) / span));
      if (bar) bar.style.setProperty('--rozet-progress', `${(done * 100).toFixed(1)}%`);
      set('[data-rozet-next]', `${next.name} için ${formatPoints(next.min - points)} puan · rozet yükselince 2 fidan`);
    } else {
      if (bar) bar.style.setProperty('--rozet-progress', '100%');
      set('[data-rozet-next]', 'En yüksek rozettesin. Teşekkürler.');
    }
    page.querySelectorAll('[data-rozet-step]').forEach((node) => {
      const i = Number(node.dataset.rozetStep);
      node.classList.toggle('is-done', i < index);
      node.classList.toggle('is-current', i === index);
    });
    const codeNode = page.querySelector('[data-rozet-code]');
    if (codeNode) {
      codeNode.textContent = state.code || 'Hazırlanıyor';
      codeNode.closest('[data-rozet-code-row]')?.toggleAttribute('data-pending', !state.code);
    }
  }

  function bindConsent() {
    const form = document.querySelector('[data-rozet-consent]');
    if (!form) return;
    const box = form.querySelector('input[type="checkbox"]');
    const status = form.querySelector('[data-rozet-consent-status]');
    const initial = Boolean(box?.checked);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!box) return;
      if (box.checked === initial && status) {
        status.textContent = 'Değişiklik yok.';
        return;
      }
      if (status) status.textContent = 'Kaydediliyor…';
      try {
        await join(box.checked);
        if (status) status.textContent = box.checked ? 'Bülten tercihin kaydedildi.' : 'Bülten aboneliğin kapatıldı.';
      } catch (_) {
        if (status) status.textContent = 'Şu an kaydedilemedi, birazdan tekrar dene.';
      }
    });
  }

  // ---------------------------------------------------------------------
  // E-mail consent prompt: members who have not opted in are asked once,
  // in a small card. Only an explicit "Evet" subscribes; "Şimdilik hayır"
  // just stops asking on this browser.
  // ---------------------------------------------------------------------

  const ASKED_KEY = 'wm-rozet-consent-asked';
  const askedFor = () => {
    try { return localStorage.getItem(ASKED_KEY) === String(state.customerId); } catch (_) { return false; }
  };
  const markAsked = () => {
    try { localStorage.setItem(ASKED_KEY, String(state.customerId)); } catch (_) {}
  };
  const cookieBannerOpen = () => {
    const banner = document.getElementById('shopify-pc__banner');
    return Boolean(banner && banner.offsetParent !== null);
  };

  function showConsentPrompt() {
    const card = document.createElement('aside');
    card.className = 'wm-rozet-ask';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-labelledby', 'wm-rozet-ask-title');
    card.innerHTML = `
      <button type="button" class="wm-rozet-ask__close" aria-label="Kapat" data-ask-no>×</button>
      <p class="wm-rozet-ask__title" id="wm-rozet-ask-title">Üyeliğin hazır.</p>
      <p class="wm-rozet-ask__text">Yeni hasat, sınırlı lot ve kampanya haberlerini e-postayla almak ister misin? Evet dersen hesabına 50 puan ekliyoruz.</p>
      <div class="wm-rozet-ask__row">
        <button type="button" class="wm-rozet-page__button" data-ask-yes>Evet, isterim</button>
        <button type="button" class="wm-rozet-page__button wm-rozet-page__button--quiet" data-ask-no>Şimdilik hayır</button>
      </div>
      <p class="wm-rozet-ask__status" role="status" aria-live="polite"></p>`;
    const status = card.querySelector('.wm-rozet-ask__status');
    const close = () => card.remove();
    card.querySelectorAll('[data-ask-no]').forEach((button) => button.addEventListener('click', () => {
      markAsked();
      close();
    }));
    card.querySelector('[data-ask-yes]').addEventListener('click', async (event) => {
      event.currentTarget.disabled = true;
      status.textContent = 'Kaydediliyor…';
      try {
        const before = Number(state.points) || 0;
        await join(true);
        markAsked();
        state.acceptsMarketing = true;
        status.textContent = (Number(state.points) || 0) > before
          ? 'Kaydedildi. 50 puan hesabına eklendi.'
          : 'Kaydedildi, teşekkürler.';
        setTimeout(close, 2500);
      } catch (_) {
        event.currentTarget.disabled = false;
        status.textContent = 'Şu an kaydedilemedi, birazdan tekrar dene.';
      }
    });
    document.body.appendChild(card);
  }

  function maybeAskConsent() {
    if (!state.signedIn || state.acceptsMarketing || !launched() || askedFor()) return;
    if (document.querySelector('[data-rozet-consent]')) return; // the member page has its own form
    let tries = 0;
    const attempt = () => {
      if (cookieBannerOpen() && tries++ < 20) {
        setTimeout(attempt, 3000);
        return;
      }
      showConsentPrompt();
    };
    setTimeout(attempt, 4000);
  }

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------

  window.WemondRozet = { renderCart, reconcileCart };

  async function boot() {
    renderPage();
    bindConsent();

    if (!state.signedIn) {
      writeSession(JOIN_KEY, null);
      if ((state.cartCodes || []).some(isMemberCode)) {
        try { await reconcileCart(await fetchCart()); } catch (_) {}
      }
      return;
    }

    // Once per session; after a failure wait a few minutes before retrying.
    const marker = readSession(JOIN_KEY) || '';
    const failedAt = marker.startsWith(`fail:${state.customerId}:`) ? Number(marker.split(':')[2]) : 0;
    const needsJoin = marker !== String(state.customerId) && Date.now() - failedAt > 5 * 60 * 1000;
    if (needsJoin) {
      try {
        await join(null);
        renderPage();
      } catch (_) {
        writeSession(JOIN_KEY, `fail:${state.customerId}:${Date.now()}`);
      }
    }
    maybeAskConsent();
    if (state.code && launched() && Number(state.cartCount) > 0 && (state.cartCodes || []).length === 0) {
      try { await reconcileCart(await fetchCart()); } catch (_) {}
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
